<script setup lang="ts">
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue';
import { useCsvStore } from '../../../stores';
import { closeModal, updateShow } from '../../../app/components/modal/modals';
import type { ModalProps } from '../../../app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DATA_QA } from './csv-rename.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';
import CsvNameInput from '../CsvNameInput/CsvNameInput.vue';
import { showAutoDismissToast, showErrorToast } from '@/noto';
import { useI18N } from '@dialpad/i18n';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';

const log = logger.getChild('DeleteCsv');
log.debug('Mounting');

const props = defineProps<ModalProps>();

if (!props.meta?.id) {
  void closeModal();
  throw new Error('Missing csv ID');
}

const { $t } = useI18N();

const csvStore = useCsvStore();

const csv = csvStore.watch(() => props.meta?.id ?? '', 'CsvRenames');
const mounted = ref(false);

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);
const cvsNameError = ref<string | null>(null);

const name = ref('');

watch(
  () => csv.value.error,
  (error) => {
    if (error) {
      log.log('Error fetching csv', error);
      void closeModal();
      // show toast
    }
  },
);

watch(
  [() => csv.value.item, () => csv.value.loading],
  ([csvToRename, loading]) => {
    if (!loading && !csvToRename) {
      log.log('CSV was deleted');
      void closeModal();
      // show toast
    }
  },
);

const confirm = async () => {
  log.debug('Confirm');
  if (!csv.value.item?.csvId) {
    log.error('csv ID is missing');
    return;
  }

  validationTrigger.value++;
  await nextTick();

  if (cvsNameError.value) {
    log.debug('Name is invalid');
    return;
  }

  void closeModal();
  log.debug('Renaming csv', csv.value.item.id);
  csvStore.update(
    csv.value.item.id,
    { name: name.value },
    {
      onSuccess: () => {
        void showAutoDismissToast({
          content: {
            title: $t('CSV_RENAME_SUCCESS_TITLE'),
          },
        });
      },
      onRetry: () => {
        // show toast
      },
      onError: (error) => {
        log.log('Error discarding csv', error);
        void showErrorToast({
          content: {
            title: $t('CSV_RENAME_ERROR_TITLE'),
          },
        });
      },
    },
  );
};

const cancel = () => {
  log.debug('Cancel');
  void closeModal();
};

watch(
  () => csv.value.item?.name,
  () => {
    name.value = csv.value.item?.name ?? '';
  },
  { immediate: true },
);

onMounted(() => {
  log.debug('Mounted');
  mounted.value = true;
});
const show = computed(() => mounted.value && !!csv.value.item);
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('CSV_RENAME_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    <div>
      <CsvNameInput v-model="name" @update:error="cvsNameError = $event" />
    </div>
    <footer class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CANCEL" importance="clear" @click="cancel">
        {{ $t('CSV_RENAME_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        importance="primary"
        class="d-ml6"
        @click="confirm"
      >
        {{ $t('CSV_RENAME_CONFIRM') }}
      </DtButton>
    </footer>
  </DtModal>
</template>
